import React, { useEffect, useState } from 'react';
import { DatePicker as CustomDatePicker } from '../../../../components/dateTimePickers';
import { UserAdjustmentInput, UserAdjustmentType, DeductionType } from '../../../../models/payroll';
import { useTranslation } from 'react-i18next';
import { Dialog, DialogContent, DialogHeader, DialogBody, DialogFooter, DialogTitle } from '../../../../components/ui/Dialog';
import Input from '../../../../components/ui/Input';
import Select from '../../../../components/ui/Select';
import Button from '../../../../components/ui/Button';

// Shared option shape for Select
interface AdjustmentTypeOption {
  label: string;
  value: string;
}

// Add Dialog Props
interface AddAdjustmentDialogProps {
  open: boolean;
  loading?: boolean;
  onClose: () => void;
  onSubmit: (input: Omit<UserAdjustmentInput, 'id'>) => Promise<void> | void;
  userId: string;
}

// Edit Dialog Props
interface EditAdjustmentDialogProps {
  open: boolean;
  loading?: boolean;
  onClose: () => void;
  onSubmit: (input: UserAdjustmentInput) => Promise<void> | void;
  value: UserAdjustmentInput | null;
}

interface DeleteDialogProps {
  open: boolean;
  loading?: boolean;
  onClose: () => void;
  onConfirm: () => Promise<void> | void;
  message?: string;
}

const buildTypeOptions = (): AdjustmentTypeOption[] =>
  Object.values(UserAdjustmentType).map((x: any) => ({
    label: x.toString(),
    value: x.toString(),
  }));

// FDL 33/2021 Art. 25 — classification drives whether a deduction counts toward the 50% cap.
const buildDeductionTypeOptions = (t: (key: string, fallback?: string) => string): AdjustmentTypeOption[] => [
  { label: t('payroll.deductionType.Standard', 'Standard'), value: DeductionType.Standard },
  { label: t('payroll.deductionType.CourtOrder', 'Court Order (exempt from 50% cap)'), value: DeductionType.CourtOrder },
  { label: t('payroll.deductionType.Disciplinary', 'Disciplinary'), value: DeductionType.Disciplinary },
  { label: t('payroll.deductionType.Loan', 'Loan'), value: DeductionType.Loan },
  { label: t('payroll.deductionType.Other', 'Other'), value: DeductionType.Other },
];

export const AddAdjustmentDialog: React.FC<AddAdjustmentDialogProps> = ({ open, loading, onClose, onSubmit, userId }) => {
  const { t } = useTranslation('common');
  const [amount, setAmount] = useState<number | ''>('');
  const [type, setType] = useState<UserAdjustmentType | null>(UserAdjustmentType.Addition);
  const [deductionType, setDeductionType] = useState<DeductionType>(DeductionType.Standard);
  const [remarks, setRemarks] = useState('');
  const [effectiveDate, setEffectiveDate] = useState<Date>(new Date());
  const [touched, setTouched] = useState(false);

  const options = buildTypeOptions();
  const deductionTypeOptions = buildDeductionTypeOptions(t);

  useEffect(() => {
    if (!open) {
      setAmount('');
      setType(UserAdjustmentType.Addition);
      setDeductionType(DeductionType.Standard);
      setRemarks('');
      setEffectiveDate(new Date());
      setTouched(false);
    }
  }, [open]);

  const hasError = touched && (!amount || amount === 0 || !type);
  const isDeduction = type === UserAdjustmentType.Deduction;

  const handleSubmit = async () => {
    setTouched(true);
    if (hasError || !type || !amount) return;
    await onSubmit({
      amount: amount as number,
      userAdjustmentType: type,
      deductionType: isDeduction ? deductionType : undefined,
      remarks,
      effectiveDate,
      userId,
    });
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent size="sm">
        <DialogHeader>
          <DialogTitle>{t('ADD ADJUSTMENT')}</DialogTitle>
        </DialogHeader>
        <DialogBody>
          <div className="flex flex-col gap-4">
            <Select
              label={t('Type')}
              options={options}
              value={type?.toString() || undefined}
              onChange={(value: any) => setType((value as UserAdjustmentType) || null)}
              searchable
            />
            {isDeduction && (
              <Select
                label={t('payroll.adjustments.deductionType', 'Deduction Classification')}
                options={deductionTypeOptions}
                value={deductionType}
                onChange={(value: unknown) => setDeductionType((value as DeductionType) || DeductionType.Standard)}
                searchable
              />
            )}
            <div>
              <Input
                label={t('Amount')}
                type="number"
                value={amount}
                onBlur={() => setTouched(true)}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => setAmount(e.target.value === '' ? '' : Number(e.target.value))}
                error={hasError ? t('Amount is required and must be > 0') : undefined}
              />
            </div>
            <div>
              <label className="text-sm font-medium text-foreground mb-1 block">{t('Remarks')}</label>
              <textarea
                rows={2}
                value={remarks}
                onChange={(e) => setRemarks(e.target.value)}
                className="flex w-full rounded-md border border-input bg-background px-3 py-2 text-sm placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/20 focus-visible:border-primary disabled:cursor-not-allowed disabled:opacity-50"
              />
            </div>
            <CustomDatePicker
              label={t('Effective Date')}
              value={effectiveDate}
              onChange={(d: any) => setEffectiveDate(d ?? new Date())}
            />
          </div>
        </DialogBody>
        <DialogFooter>
          <div className="flex gap-3">
            <Button variant="outline" onClick={onClose} disabled={loading}>{t('Cancel')}</Button>
            <Button variant="default" onClick={handleSubmit} disabled={loading || hasError} loading={loading}>{t('Submit')}</Button>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export const EditAdjustmentDialog: React.FC<EditAdjustmentDialogProps> = ({ open, loading, onClose, onSubmit, value }) => {
  const { t } = useTranslation('common');
  const [form, setForm] = useState<UserAdjustmentInput | null>(value);
  const [touched, setTouched] = useState(false);

  const deductionTypeOptions = buildDeductionTypeOptions(t);

  useEffect(() => {
    if (open) setForm(value);
  }, [open, value]);

  if (!form) return null;

  const hasError = touched && (!form.amount || form.amount === 0);
  const isDeduction = form.userAdjustmentType === UserAdjustmentType.Deduction;

  const handleChange = (patch: Partial<UserAdjustmentInput>) => setForm(prev => ({ ...(prev as UserAdjustmentInput), ...patch }));

  const handleSubmit = async () => {
    setTouched(true);
    if (hasError) return;
    await onSubmit(form as UserAdjustmentInput);
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent size="sm">
        <DialogHeader>
          <DialogTitle>{t('EDIT ADJUSTMENT')}</DialogTitle>
        </DialogHeader>
        <DialogBody>
          <div className="flex flex-col gap-4">
            <Input
              label={t('Type')}
              value={form.userAdjustmentType}
              disabled
            />
            {isDeduction && (
              <Select
                label={t('payroll.adjustments.deductionType', 'Deduction Classification')}
                options={deductionTypeOptions}
                value={form.deductionType ?? DeductionType.Standard}
                onChange={(value: unknown) => handleChange({ deductionType: (value as DeductionType) || DeductionType.Standard })}
                searchable
              />
            )}
            <Input
              label={t('Amount')}
              type="number"
              value={form.amount}
              onBlur={() => setTouched(true)}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => handleChange({ amount: Number(e.target.value) })}
              error={hasError ? t('Amount is required and must be > 0') : undefined}
            />
            <div>
              <label className="text-sm font-medium text-foreground mb-1 block">{t('Remarks')}</label>
              <textarea
                rows={2}
                value={form.remarks}
                onChange={(e) => handleChange({ remarks: e.target.value })}
                className="flex w-full rounded-md border border-input bg-background px-3 py-2 text-sm placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/20 focus-visible:border-primary disabled:cursor-not-allowed disabled:opacity-50"
              />
            </div>
            <CustomDatePicker
              label={t('Effective Date')}
              value={form.effectiveDate ? form.effectiveDate : null}
              onChange={(d: any) => handleChange({ effectiveDate: d ?? new Date() })}
            />
          </div>
        </DialogBody>
        <DialogFooter>
          <div className="flex gap-3">
            <Button variant="outline" onClick={onClose} disabled={loading}>{t('Cancel')}</Button>
            <Button variant="default" onClick={handleSubmit} disabled={loading || hasError} loading={loading}>{t('Submit')}</Button>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export const DeleteAdjustmentDialog: React.FC<DeleteDialogProps> = ({ open, loading, onClose, onConfirm, message }) => {
  const { t } = useTranslation('common');
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent size="sm">
        <DialogBody>
          <p className="text-sm text-muted-foreground">
            {t(message || 'Are you sure you want to delete this record?')}
          </p>
        </DialogBody>
        <DialogFooter>
          <div className="flex gap-3">
            <Button variant="outline" onClick={onClose} disabled={loading}>{t('Cancel')}</Button>
            <Button variant="destructive" onClick={() => onConfirm()} disabled={loading} loading={loading}>{t('Delete')}</Button>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
